import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { History, Loader2, RefreshCw } from "lucide-react";

type Row = {
  id: string;
  route_id: string | null;
  action: "insert" | "update" | "delete" | "enable" | "disable";
  actor_id: string | null;
  changed_fields: string[];
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  created_at: string;
};

const ACTION_VARIANT: Record<Row["action"], string> = {
  insert: "bg-primary/15 text-primary",
  update: "bg-muted text-foreground",
  enable: "bg-primary/15 text-primary",
  disable: "bg-amber-500/15 text-amber-600",
  delete: "bg-destructive/15 text-destructive",
};

function labelFor(row: Row): string {
  const v = row.new_values ?? row.old_values ?? {};
  return (v as { label?: string }).label ?? "(unknown route)";
}

export function HelpdeskAlertRouteAuditPanel() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("helpdesk_alert_route_audit")
      .select("id, route_id, action, actor_id, changed_fields, old_values, new_values, created_at")
      .order("created_at", { ascending: false })
      .limit(100);
    setLoading(false);
    if (error) toast.error(`Load audit: ${error.message}`);
    else setRows((data ?? []) as Row[]);
  };

  useEffect(() => { void load(); }, []);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <History className="size-4 text-primary" /> Alert route change log
          </CardTitle>
          <CardDescription>
            Every add / edit / enable / disable / delete on an alert route is recorded here with the
            admin user id and timestamp. Webhook URLs are masked in the audit trail.
          </CardDescription>
        </div>
        <Button variant="ghost" size="icon-sm" onClick={() => void load()} aria-label="Refresh">
          {loading ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
        </Button>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
                <th className="py-2 pr-4 font-medium">When</th>
                <th className="py-2 pr-4 font-medium">Action</th>
                <th className="py-2 pr-4 font-medium">Route</th>
                <th className="py-2 pr-4 font-medium">Changed</th>
                <th className="py-2 pr-4 font-medium">Actor</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="py-4 text-center"><Loader2 className="size-4 animate-spin inline" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} className="py-4 text-center text-xs text-muted-foreground">No route changes recorded yet.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className="border-b border-border/50 last:border-0">
                  <td className="py-2 pr-4 font-mono text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(r.created_at).toISOString().replace("T", " ").slice(0, 19)}
                  </td>
                  <td className="py-2 pr-4">
                    <Badge className={ACTION_VARIANT[r.action]}>{r.action}</Badge>
                  </td>
                  <td className="py-2 pr-4">{labelFor(r)}</td>
                  <td className="py-2 pr-4 text-xs text-muted-foreground">
                    {r.changed_fields?.length ? r.changed_fields.join(", ") : "—"}
                  </td>
                  <td className="py-2 pr-4 font-mono text-[11px] text-muted-foreground">
                    {r.actor_id ? r.actor_id.slice(0, 8) + "…" : "system"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export default HelpdeskAlertRouteAuditPanel;
